import type { ApiResponse } from "@/types/api/common";
import type {
	ApplyStarterPackRequest,
	ApplyStarterPackResult,
	AssetImportRequest,
	AssetImportResult,
	AssetImportTemplate,
	ChecklistImportReport,
	ChecklistImportRequest,
	ClearSmokeTestRequest,
	ClearSmokeTestResult,
	CreateIssueRoleAssignmentRequest,
	CreateTenantLocationRequest,
	EnsureStarterUserGroupsResult,
	HeldInvitationsResult,
	IssueRole,
	IssueRoleAssignment,
	IssueRoleAssignmentsResult,
	IssueRoleDetail,
	ResetClientConfigurationRequest,
	ResetClientConfigurationResult,
	ResetMode,
	SendInvitationBatchResult,
	SetupStatusResult,
	TenantAsset,
	TenantAssetGroup,
	TenantChecklist,
	TenantInspectionConfiguration,
	TenantIssueType,
	TenantLocation,
	TenantPagedResult,
	TenantUser,
	TenantUserGroup,
	TenantUserGroupDetail,
} from "@/types/api/onboarding";
import { apiFetch } from "./api-client";

/**
 * The Client handlers behind the tenant proxies paginate with
 * `PaginationParameters` (`pageNumber`, `pageSize` capped at 50 server-side).
 * The stepper always asks for the first 50 rows and shows a "showing N of
 * total" hint when the tenant has more.
 */
export const TENANT_PAGE_SIZE = 50;

const tenantPage = (extra?: Record<string, string>) =>
	new URLSearchParams({
		pageNumber: "1",
		pageSize: String(TENANT_PAGE_SIZE),
		...extra,
	}).toString();

/**
 * Tenant Setup console endpoints (Phase 1A). All of these are
 * Admin-namespace routes: the client id travels in the PATH and the handler
 * impersonates the tenant itself (`ImpersonateClient(request.ClientId)`),
 * so they do not depend on the `X-Tenant-ID` header that `apiFetch` adds
 * for the selected client. Every route requires `system:clients:impersonate`.
 *
 * Responses are the ResultWrapper envelope; only `.result` is returned.
 * Non-2xx responses surface as `ApiError` (see api-client.ts) — notably
 * 400 (`messages[]` = validation list), 404 (unknown client), 409
 * (starter pack: a provisioning run is still Running; import: name conflict
 * with `allowRename=false`, the report is in `body.result`) and 422
 * (FluentValidation, e.g. an empty invitation batch).
 */
export const onboardingService = {
	/** GET /clients/{clientId}/setup-status — the 9 computed readiness gates. */
	getSetupStatus: async (clientId: string): Promise<SetupStatusResult> => {
		const response = await apiFetch<ApiResponse<SetupStatusResult>>(
			`/clients/${clientId}/setup-status`,
		);
		return response.result;
	},

	/**
	 * POST /clients/{clientId}/onboarding/starter-pack — copies every published
	 * starter-pack checklist of the industry (default: the client's stored
	 * industry) into the tenant. Idempotent per run key; per-item failures are
	 * rows (`failed: true`), never a 500.
	 */
	applyStarterPack: async (
		clientId: string,
		data?: ApplyStarterPackRequest,
	): Promise<ApplyStarterPackResult> => {
		const response = await apiFetch<ApiResponse<ApplyStarterPackResult>>(
			`/clients/${clientId}/onboarding/starter-pack`,
			{ method: "POST", body: JSON.stringify(data ?? {}) },
		);
		return response.result;
	},

	/**
	 * POST /clients/{clientId}/onboarding/checklists/import-json?dryRun=
	 * Body = the UpsertNested wire shape (+ allowRename, translations).
	 * `dryRun=true` returns the pre-flight report without writing; execute
	 * always creates a NEW checklist (every id in the payload is remapped).
	 */
	importChecklistJson: async (
		clientId: string,
		payload: ChecklistImportRequest,
		dryRun: boolean,
	): Promise<ChecklistImportReport> => {
		const q = new URLSearchParams({ dryRun: String(dryRun) });
		const response = await apiFetch<ApiResponse<ChecklistImportReport>>(
			`/clients/${clientId}/onboarding/checklists/import-json?${q}`,
			{ method: "POST", body: JSON.stringify(payload) },
		);
		return response.result;
	},

	/**
	 * POST /clients/{clientId}/onboarding/assets/import?dryRun= — .xlsx / .csv as
	 * base64. A dry run returns the per-row preview; execute creates the valid
	 * new rows (existing unit numbers skipped, error rows reported).
	 */
	importAssets: async (
		clientId: string,
		payload: AssetImportRequest,
		dryRun: boolean,
	): Promise<AssetImportResult> => {
		const q = new URLSearchParams({ dryRun: String(dryRun) });
		const response = await apiFetch<ApiResponse<AssetImportResult>>(
			`/clients/${clientId}/onboarding/assets/import?${q}`,
			{ method: "POST", body: JSON.stringify(payload) },
		);
		return response.result;
	},

	/** GET /clients/{clientId}/onboarding/assets/import-template — the tenant's .xlsx template (base64). */
	getAssetImportTemplate: async (
		clientId: string,
	): Promise<AssetImportTemplate> => {
		const response = await apiFetch<ApiResponse<AssetImportTemplate>>(
			`/clients/${clientId}/onboarding/assets/import-template`,
		);
		return response.result;
	},

	/**
	 * GET /clients/{clientId}/onboarding/invitations — active, undeleted users
	 * whose invitation is None (held) or Pending (sent, not accepted). Kiosk
	 * users have no invitation status and are therefore absent.
	 */
	getHeldInvitations: async (
		clientId: string,
	): Promise<HeldInvitationsResult> => {
		const response = await apiFetch<ApiResponse<HeldInvitationsResult>>(
			`/clients/${clientId}/onboarding/invitations`,
		);
		return response.result;
	},

	/**
	 * POST /clients/{clientId}/onboarding/invitations/send-batch — sends (or
	 * re-sends) the invitation to each listed user. Unknown ids and e-mail /
	 * token failures land in `failed[]`; kiosk / inactive / revoked / accepted /
	 * no-email users in `skipped[]`.
	 */
	sendInvitationBatch: async (
		clientId: string,
		clientUserIds: string[],
	): Promise<SendInvitationBatchResult> => {
		const response = await apiFetch<ApiResponse<SendInvitationBatchResult>>(
			`/clients/${clientId}/onboarding/invitations/send-batch`,
			{ method: "POST", body: JSON.stringify({ clientUserIds }) },
		);
		return response.result;
	},

	// ── Phase 1B ──────────────────────────────────────────────────────────

	/**
	 * POST /clients/{clientId}/reset?mode=Preview|Execute — runs
	 * `usp_ResetClientConfiguration`. Preview only counts (no body needed);
	 * Execute deletes and requires the exact client name in the body plus
	 * `system:clients:delete`, and `acknowledgeProtected: true` when the
	 * Preview reported `isPurgeProtected`. 409 = the configured Library tenant
	 * or an unacknowledged purge protection (message says "purge-protected"),
	 * 400 = name mismatch.
	 */
	resetClientConfiguration: async (
		clientId: string,
		mode: ResetMode,
		body?: ResetClientConfigurationRequest,
	): Promise<ResetClientConfigurationResult> => {
		const q = new URLSearchParams({ mode });
		const payload: ResetClientConfigurationRequest =
			mode === "Execute"
				? {
						confirmClientName: body?.confirmClientName,
						acknowledgeProtected:
							body?.acknowledgeProtected ?? false,
					}
				: {};
		const response = await apiFetch<
			ApiResponse<ResetClientConfigurationResult>
		>(`/clients/${clientId}/reset?${q}`, {
			method: "POST",
			body: JSON.stringify(payload),
		});
		return response.result;
	},

	/**
	 * POST /clients/{clientId}/clear-smoke-test?mode=Preview|Execute — runs
	 * `usp_ClearClientOperationalData`. Preview counts (and includes the
	 * listed users so the plan is complete); Execute deletes and needs the
	 * exact client name plus `system:clients:delete`, and
	 * `acknowledgeProtected: true` when the Preview reported
	 * `isPurgeProtected`. 409 = Library tenant ("Checklist Library") or
	 * unacknowledged purge protection ("purge-protected"); 400 = name
	 * mismatch or an offending user id (owner / not in tenant).
	 */
	clearSmokeTestData: async (
		clientId: string,
		mode: ResetMode,
		body: ClearSmokeTestRequest,
	): Promise<ClearSmokeTestResult> => {
		const q = new URLSearchParams({ mode });
		const payload: ClearSmokeTestRequest =
			mode === "Execute"
				? {
						confirmClientName: body.confirmClientName,
						acknowledgeProtected:
							body.acknowledgeProtected ?? false,
						removeClientUserIds: body.removeClientUserIds,
					}
				: { removeClientUserIds: body.removeClientUserIds };
		const response = await apiFetch<ApiResponse<ClearSmokeTestResult>>(
			`/clients/${clientId}/clear-smoke-test?${q}`,
			{ method: "POST", body: JSON.stringify(payload) },
		);
		return response.result;
	},

	// The remaining calls are tenant proxies: the tenant is the `X-Tenant-ID`
	// header (selected client in the admin store), the `clientId` argument only
	// keys the react-query cache.

	/** GET /locations (Client `Location/GetAll`). */
	getTenantLocations: async (): Promise<
		TenantPagedResult<TenantLocation>
	> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantLocation>>
		>(`/locations?${tenantPage()}`);
		return response.result;
	},

	/** POST /locations (Client `Location/Create` shape: `address`, `locationNumber`). */
	createTenantLocation: async (
		data: CreateTenantLocationRequest,
	): Promise<TenantLocation> => {
		const response = await apiFetch<ApiResponse<TenantLocation>>(
			"/locations",
			{ method: "POST", body: JSON.stringify(data) },
		);
		return response.result;
	},

	/** GET /asset-groups (Client `AssetGroup/GetAll`). */
	getTenantAssetGroups: async (): Promise<
		TenantPagedResult<TenantAssetGroup>
	> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantAssetGroup>>
		>(`/asset-groups?${tenantPage()}`);
		return response.result;
	},

	/** GET /assets (Client `Asset/GetAll`). */
	getTenantAssets: async (): Promise<TenantPagedResult<TenantAsset>> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantAsset>>
		>(`/assets?${tenantPage()}`);
		return response.result;
	},

	/** GET /users (Client `Users/GetAll`). */
	getTenantUsers: async (): Promise<TenantPagedResult<TenantUser>> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantUser>>
		>(`/users?${tenantPage()}`);
		return response.result;
	},

	/** GET /usergroups (Client `UserGroup/GetAll`). */
	getTenantUserGroups: async (): Promise<
		TenantPagedResult<TenantUserGroup>
	> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantUserGroup>>
		>(`/usergroups?${tenantPage()}`);
		return response.result;
	},

	/** GET /usergroups/{id} (Client `UserGroup/GetById`, with members). */
	getTenantUserGroup: async (
		userGroupId: string,
	): Promise<TenantUserGroupDetail> => {
		const response = await apiFetch<ApiResponse<TenantUserGroupDetail>>(
			`/usergroups/${userGroupId}`,
		);
		return response.result;
	},

	/** GET /checklists (Client `Checklist/GetAll`, with `updateAvailable`). */
	getTenantChecklists: async (): Promise<
		TenantPagedResult<TenantChecklist>
	> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantChecklist>>
		>(`/checklists?${tenantPage()}`);
		return response.result;
	},

	/**
	 * GET /checklists with filters (Client `Checklist/GetAll` binds
	 * `PageNumber`, `PageSize` ≤ 50, `SearchTerm`, `IsActive`).
	 */
	listTenantChecklists: async (query: {
		pageNumber?: number;
		searchTerm?: string;
		isActive?: boolean;
	}): Promise<TenantPagedResult<TenantChecklist>> => {
		const params = new URLSearchParams({
			PageNumber: String(query.pageNumber ?? 1),
			PageSize: String(TENANT_PAGE_SIZE),
		});
		if (query.searchTerm?.trim())
			params.set("SearchTerm", query.searchTerm.trim());
		if (query.isActive !== undefined)
			params.set("IsActive", String(query.isActive));
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantChecklist>>
		>(`/checklists?${params}`);
		return response.result;
	},

	/** GET /inspection-configurations (Client `InspectionConfiguration/GetAll`). */
	getTenantInspectionConfigurations: async (): Promise<
		TenantPagedResult<TenantInspectionConfiguration>
	> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantInspectionConfiguration>>
		>(`/inspection-configurations?${tenantPage()}`);
		return response.result;
	},

	/** GET /issue-roles (Client `IssueRole/GetAll`). */
	getIssueRoles: async (): Promise<TenantPagedResult<IssueRole>> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<IssueRole>>
		>(`/issue-roles?${tenantPage()}`);
		return response.result;
	},

	/** GET /issue-roles/{roleId} (Client `IssueRole/GetById`, incl. notification rules). */
	getIssueRole: async (roleId: string): Promise<IssueRoleDetail> => {
		const response = await apiFetch<ApiResponse<IssueRoleDetail>>(
			`/issue-roles/${roleId}`,
		);
		return response.result;
	},

	/** GET /issue-roles/{roleId}/assignments (Client `IssueRoleAssignment/GetByRole`). */
	getIssueRoleAssignments: async (
		roleId: string,
	): Promise<IssueRoleAssignmentsResult> => {
		const response = await apiFetch<
			ApiResponse<IssueRoleAssignmentsResult>
		>(`/issue-roles/${roleId}/assignments`);
		return response.result;
	},

	/** POST /issue-roles/{roleId}/assignments (Client `IssueRoleAssignment/Create`). */
	createIssueRoleAssignment: async (
		roleId: string,
		data: CreateIssueRoleAssignmentRequest,
	): Promise<IssueRoleAssignment> => {
		const response = await apiFetch<ApiResponse<IssueRoleAssignment>>(
			`/issue-roles/${roleId}/assignments`,
			{ method: "POST", body: JSON.stringify(data) },
		);
		return response.result;
	},

	/** GET /issue-types (Client `IssueType/GetAll`). */
	getTenantIssueTypes: async (): Promise<
		TenantPagedResult<TenantIssueType>
	> => {
		const response = await apiFetch<
			ApiResponse<TenantPagedResult<TenantIssueType>>
		>(`/issue-types?${tenantPage()}`);
		return response.result;
	},

	/**
	 * POST /clients/{clientId}/onboarding/user-groups/ensure-starter — creates
	 * the starter pack's user groups (Inspectors, Admins, Trainers, Managers,
	 * each with its system role) the client is missing. Existing groups are
	 * left as they are; re-running changes nothing.
	 */
	ensureStarterUserGroups: async (
		clientId: string,
	): Promise<EnsureStarterUserGroupsResult> => {
		const response = await apiFetch<
			ApiResponse<EnsureStarterUserGroupsResult>
		>(`/clients/${clientId}/onboarding/user-groups/ensure-starter`, {
			method: "POST",
		});
		return response.result;
	},
};
